/**
 * PlanningPage Component
 * Full-page planning experience: retirement, college, and other life events.
 *
 * Three labelled plan regions, in this order (WP-PLAN-1):
 * 1. Your goals    - what the user is planning for: retirement age, college,
 *                    other life events
 * 2. Projection    - what those goals and the assumptions below produce
 * 3. Assumptions   - the estimates driving the projection. Named "Assumptions"
 *                    rather than "Inputs" so the user reads them as estimates
 *                    they can change, not facts.
 *
 * Followed by one region that is deliberately NOT part of the plan (WP-PLAN-2):
 * 4. Explore scenarios - a sandbox of what-ifs over the saved plan, styled
 *                    apart from it. It changes the plan only through its
 *                    explicit "Set my plan based on this scenario" action.
 *
 * Age is always derived from birth year (never manually entered).
 *
 * NOTE: Social Security, pensions and other retirement income live in
 * Assumptions, NOT as user-added life events. The projection adds assumption
 * income AND events, so entering one in both places double-counts it.
 */

import { useState, useMemo, useCallback, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { useHasEntitlement } from '@/hooks/useBilling';
import {
  useRetirementInputs,
  useRetirementNetWorth,
  useUpdateRetirementInputs,
  useRetirementEvents,
  useBirthYear,
  useUpdateBirthYear,
} from '@/hooks/useRetirementPlanning';
import { buildProjectionGrid, calculateRequiredMonthlySavings } from '@/utils/retirementCalculations';
import { centsToDollars } from '@/utils/format';
import type { RetirementPlanningInputs, RetirementInputsUpdate } from '@/types/retirement';

import { Select } from '@/components/ui';
import Button from '@/components/ui/Button';
import RetirementAgeSlider from '@/components/retirement/RetirementAgeSlider';
import RetirementInputPanel from '@/components/dashboard/RetirementInputPanel';
import RetirementHeatmap from '@/components/dashboard/RetirementHeatmap';
import HeatmapLegend from '@/components/dashboard/HeatmapLegend';
import RetirementDisclaimer from '@/components/dashboard/RetirementDisclaimer';
import GoalCalculator from '@/components/landing/GoalCalculator';
import RetirementEventsPanel from '@/components/retirement/RetirementEventsPanel';
import LeverPanel from '@/components/retirement/LeverPanel';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_RETIREMENT_AGE = 65;

const DEFAULT_INPUTS: RetirementInputsUpdate = {
  currentAge: 35,
  retirementAge: DEFAULT_RETIREMENT_AGE,
  netWorthAdjustmentCents: 0,
  socialSecurityAnnualCents: 2_400_000,
  pensionAnnualCents: 0,
  otherRetirementIncomeCents: 0,
  retirementExpensesAnnualCents: 6_000_000,
  currentSavingsAnnualCents: 1_200_000,
};

// ============================================================================
// Helper Functions
// ============================================================================

function calculateCurrentAge(birthYear: number): number {
  return new Date().getFullYear() - birthYear;
}

// ============================================================================
// Sub-Components
// ============================================================================

function UpgradePromptSection() {
  return (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="mb-6">
        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Planning</h2>
      <p className="text-gray-600 mb-6">
        Planning is available on Pro and Family plans. Upgrade to plan for
        retirement, college and other life events, and see how each one changes
        your long-term picture.
      </p>
      <Link
        to="/billing"
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        View Plans
      </Link>
    </div>
  );
}

function generateBirthYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18;
  const options: { value: string; label: string }[] = [];
  for (let year = maxYear; year >= 1930; year--) {
    options.push({ value: year.toString(), label: year.toString() });
  }
  return options;
}

function BirthYearSetupPrompt() {
  const [selectedYear, setSelectedYear] = useState<string>('');
  const updateBirthYear = useUpdateBirthYear();
  const birthYearOptions = useMemo(() => generateBirthYearOptions(), []);

  const handleSave = () => {
    if (!selectedYear) return;
    updateBirthYear.mutate(parseInt(selectedYear, 10));
  };

  return (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="mb-6">
        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Set Your Birth Year</h2>
      <p className="text-gray-600 mb-6">
        Retirement planning uses your birth year to calculate age-based projections.
      </p>
      <div className="max-w-xs mx-auto space-y-4">
        <Select
          label="Birth Year"
          id="retirement-birth-year"
          value={selectedYear}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedYear(e.target.value)}
          options={birthYearOptions}
          placeholder="Select year"
        />
        <Button
          onClick={handleSave}
          disabled={!selectedYear || updateBirthYear.isPending}
          isLoading={updateBirthYear.isPending}
        >
          Save & Continue
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function PlanningPage() {
  // ---- Entitlement check ----
  const { hasEntitlement, isLoading: entitlementLoading } = useHasEntitlement(
    'feature:retirement_planning'
  );

  // ---- Birth year from tenant profile ----
  const { birthYear, isLoading: birthYearLoading } = useBirthYear();

  // ---- Retirement data hooks ----
  const { data: inputs, isLoading: inputsLoading } = useRetirementInputs();
  const { data: netWorth, isLoading: netWorthLoading } = useRetirementNetWorth();
  const { data: events = [], isLoading: eventsLoading } = useRetirementEvents();
  const updateMutation = useUpdateRetirementInputs();

  // ---- Birth year mutation ----
  const updateBirthYear = useUpdateBirthYear();
  const birthYearOptions = useMemo(() => generateBirthYearOptions(), []);
  const handleBirthYearChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value, 10);
    if (!isNaN(year)) updateBirthYear.mutate(year);
  }, [updateBirthYear]);

  // ---- Local state ----
  const [retirementAgeOverride, setRetirementAgeOverride] = useState<number | null>(null);

  // ---- Loading state ----
  const isLoading = entitlementLoading || birthYearLoading || inputsLoading || netWorthLoading || eventsLoading;

  // ---- Derived values ----
  const hasInputs = inputs !== null && inputs !== undefined;
  const currentAge = birthYear != null ? calculateCurrentAge(birthYear) : null;

  // Retirement age: use slider override, then inputs, then default
  const retirementAge = retirementAgeOverride ?? (hasInputs ? inputs.retirementAge : DEFAULT_RETIREMENT_AGE);

  // Build effective inputs with birth-year-derived ages
  const effectiveInputs: RetirementPlanningInputs | null = useMemo(() => {
    if (!hasInputs || currentAge == null) return null;
    return { ...inputs, currentAge, retirementAge };
  }, [hasInputs, inputs, currentAge, retirementAge]);

  const isAlreadyRetired = effectiveInputs != null && effectiveInputs.currentAge >= effectiveInputs.retirementAge;

  const totalNetWorthCents = effectiveInputs && netWorth
    ? netWorth.totalCents + (effectiveInputs.netWorthAdjustmentCents ?? 0)
    : 0;

  const projections = effectiveInputs && netWorth
    ? buildProjectionGrid(
        effectiveInputs,
        totalNetWorthCents,
        events.length > 0 ? events : undefined,
        birthYear ?? undefined,
      )
    : null;

  const goalResults = useMemo(() => {
    if (!effectiveInputs || !netWorth) return null;
    return calculateRequiredMonthlySavings(effectiveInputs, totalNetWorthCents, retirementAge);
  }, [effectiveInputs, netWorth, totalNetWorthCents, retirementAge]);

  const currentMonthlySavings = effectiveInputs
    ? centsToDollars(effectiveInputs.currentSavingsAnnualCents) / 12
    : 0;

  // ---- Handlers ----

  const handleRetirementAgeChange = (age: number) => {
    setRetirementAgeOverride(age);

    if (currentAge != null && hasInputs) {
      updateMutation.mutate({
        currentAge,
        retirementAge: age,
        netWorthAdjustmentCents: inputs.netWorthAdjustmentCents,
        socialSecurityAnnualCents: inputs.socialSecurityAnnualCents,
        pensionAnnualCents: inputs.pensionAnnualCents,
        otherRetirementIncomeCents: inputs.otherRetirementIncomeCents,
        retirementExpensesAnnualCents: inputs.retirementExpensesAnnualCents,
        currentSavingsAnnualCents: inputs.currentSavingsAnnualCents,
        socialSecurityStartAge: inputs.socialSecurityStartAge,
        pensionStartAge: inputs.pensionStartAge,
        otherIncomeStartAge: inputs.otherIncomeStartAge,
        workingIncomeAnnualCents: inputs.workingIncomeAnnualCents,
      });
    }
  };

  const handleInputUpdate = (values: RetirementInputsUpdate) => {
    if (currentAge != null) {
      updateMutation.mutate({ ...values, currentAge, retirementAge });
    } else {
      updateMutation.mutate(values);
    }
  };

  const handleSetup = async () => {
    if (currentAge == null || birthYear == null) return;
    await updateMutation.mutateAsync({
      ...DEFAULT_INPUTS,
      currentAge,
      retirementAge,
    });
  };

  // ---- Render ----

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader />
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
          <div className="h-48 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!hasEntitlement) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader />
        <UpgradePromptSection />
      </div>
    );
  }

  if (birthYear == null) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader />
        <BirthYearSetupPrompt />
      </div>
    );
  }

  if (!hasInputs) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Get Started with Planning
          </h2>
          <p className="text-gray-600 mb-6 max-w-lg mx-auto">
            Add your basic assumptions to see how retirement, college and other
            life events affect your long-term picture.
          </p>
          <button
            onClick={handleSetup}
            disabled={updateMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? 'Setting up...' : 'Set Up Planning'}
          </button>
        </div>
      </div>
    );
  }

  // ---- Main content ----

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader />

      {/* WP-PLAN-1 Phase 4: three regions, in the order a person reasons about
          them - what am I planning for, what does that produce, and what is it
          assuming. The page owns all three <h2>s; every child heading is an
          <h3> so the document outline matches the visual structure. */}
      <div className="space-y-10">
        {/* ---------------------------------------------------------------
            Region 1: Your goals

            Retirement is deliberately NOT a peer of the other two. The
            projection cannot exist without a retirement age and savings
            target, whereas college and life events are modifiers layered on
            top. Three equal tiles would imply retirement is optional and would
            bury the thing the section exists for. So retirement is full-width
            and primary, and the other two sit in a secondary row inside the
            panel below it - still visible, never behind a menu.
            --------------------------------------------------------------- */}
        <section aria-labelledby="planning-goals-heading">
          <h2
            id="planning-goals-heading"
            className="text-lg font-semibold text-gray-900 mb-4"
          >
            Your goals
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Retirement
              </h3>
              <RetirementAgeSlider
                retirementAge={retirementAge}
                onRetirementAgeChange={handleRetirementAgeChange}
              />

              {effectiveInputs && (
                <GoalCalculator
                  goalRetirementAge={retirementAge}
                  onGoalRetirementAgeChange={handleRetirementAgeChange}
                  currentAge={effectiveInputs.currentAge}
                  currentMonthlySavings={currentMonthlySavings}
                  goalResults={goalResults}
                />
              )}
            </div>

            <RetirementEventsPanel />
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Region 2: Projection - what the goals and assumptions produce.
            --------------------------------------------------------------- */}
        <section aria-labelledby="planning-projection-heading">
          <h2
            id="planning-projection-heading"
            className="text-lg font-semibold text-gray-900 mb-4"
          >
            Projection
          </h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <RetirementHeatmap
              projections={projections}
              events={events}
              birthYear={birthYear}
            />
            <HeatmapLegend />
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Region 3: Assumptions - the estimates driving the projection.
            "Assumptions" rather than "Inputs" because it tells the user these
            are estimates, which is what the disclaimer says too.
            --------------------------------------------------------------- */}
        <section aria-labelledby="planning-assumptions-heading">
          <h2
            id="planning-assumptions-heading"
            className="text-lg font-semibold text-gray-900 mb-4"
          >
            Assumptions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <Select
                  label="Birth Year"
                  id="retirement-birth-year-input"
                  value={birthYear?.toString() ?? ''}
                  onChange={handleBirthYearChange}
                  options={birthYearOptions}
                  placeholder="Select year"
                />
                {currentAge != null && (
                  <p className="mt-1 text-sm text-gray-500">
                    Current age: {currentAge}
                  </p>
                )}
              </div>
              {effectiveInputs && (
                <RetirementInputPanel
                  inputs={effectiveInputs}
                  isAlreadyRetired={isAlreadyRetired}
                  onUpdate={handleInputUpdate}
                  variant="full"
                />
              )}
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            Region 4: Explore scenarios - a sandbox, NOT part of the plan.

            WP-PLAN-2: this used to be a card inside Assumptions, styled
            exactly like the saved inputs above it, so a what-if could not be
            told apart from the plan itself. It now follows the three plan
            regions as its own region, and is styled as a sandbox (dashed,
            tinted, labelled) so it cannot be mistaken for them.

            WP-RETIRE-12: recalculates locally; nothing persists until the
            user chooses "Set my plan based on this scenario". The draft stays
            in memory deliberately - see LeverPanel.
            --------------------------------------------------------------- */}
        {effectiveInputs && netWorth && (
          <section
            aria-labelledby="planning-explore-heading"
            data-testid="planning-explore-region"
            className="rounded-lg border-2 border-dashed border-primary-300 bg-primary-50 p-6"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2
                id="planning-explore-heading"
                className="text-lg font-semibold text-gray-900"
              >
                Explore scenarios
              </h2>
              <span className="text-xs font-medium text-primary-800 bg-primary-100 px-2 py-0.5 rounded">
                Not your saved plan
              </span>
            </div>
            <p className="mt-1 mb-4 text-sm text-gray-600">
              Try a what-if without touching your plan. Nothing here is saved
              unless you set your plan based on this scenario.
            </p>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <LeverPanel
                inputs={effectiveInputs}
                netWorthCents={totalNetWorthCents}
                events={events.length > 0 ? events : undefined}
                birthYear={birthYear ?? undefined}
                onSave={(update) => updateMutation.mutate(update)}
                isSaving={updateMutation.isPending}
              />
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <RetirementDisclaimer />
      </div>
    </div>
  );
}

// ============================================================================
// Page Header
// ============================================================================

function PageHeader() {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Planning</h1>
      <p className="mt-1 text-gray-600">
        Plan for retirement, college and other life events, and see how each one
        changes your long-term picture
      </p>
    </div>
  );
}
